/**
 * Central tactile feedback engine.
 *
 * Combines:
 *  - Web Vibration API (Android browsers; iOS Safari silently no-ops)
 *  - Premium synthetic micro sounds via Web Audio (iPhone/iPad fallback)
 *  - User preferences via localStorage
 *  - Throttling, reduced-motion respect, and graceful no-op
 *  - Optional Capacitor Haptics support (loaded only if available, never required)
 *
 * Usage:
 *   import { feedback } from "@/lib/tactileFeedback";
 *   feedback.light(); feedback.selection(); feedback.spinTick(); ...
 *
 * Runtime controls:
 *   feedback.setFeedbackEnabled(false)
 *   feedback.setSoundEnabled(false)
 *   feedback.setHapticsEnabled(false)
 *
 * Storage keys:
 *   frankPistoriusFeedback  "on" | "off"   (master)
 *   frankPistoriusSound     "on" | "off"
 *   frankPistoriusHaptics   "on" | "off"
 */

export type FeedbackType =
  | "light"
  | "selection"
  | "tick"
  | "medium"
  | "success"
  | "warning"
  | "error"
  | "open"
  | "close"
  | "spinStart"
  | "spinTick"
  | "spinEnd";

const KEY_FEEDBACK = "frankPistoriusFeedback";
const KEY_SOUND = "frankPistoriusSound";
const KEY_HAPTICS = "frankPistoriusHaptics";

const isBrowser = () => typeof window !== "undefined" && typeof navigator !== "undefined";
const now = () =>
  typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();

function readPref(key: string, defaultOn = true): boolean {
  if (!isBrowser()) return defaultOn;
  try {
    const v = window.localStorage?.getItem(key);
    if (v === "off") return false;
    if (v === "on") return true;
    return defaultOn;
  } catch {
    return defaultOn;
  }
}

function writePref(key: string, on: boolean) {
  if (!isBrowser()) return;
  try {
    window.localStorage?.setItem(key, on ? "on" : "off");
  } catch {
    /* ignore */
  }
}

function prefersReducedMotion(): boolean {
  if (!isBrowser()) return false;
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  } catch {
    return false;
  }
}

function isTouchDevice(): boolean {
  if (!isBrowser()) return false;
  try {
    if (window.matchMedia?.("(pointer: coarse)").matches) return true;
  } catch {
    /* ignore */
  }
  return (navigator.maxTouchPoints ?? 0) > 0;
}

const hasVibrate = () => isBrowser() && typeof navigator.vibrate === "function";

// ────────────────────────────────────────────────────────────────────────────
// Throttling
// ────────────────────────────────────────────────────────────────────────────
let lastVibAt = 0;
let lastSoundAt = 0;
const lastSoundByType: Record<string, number> = {};

// ────────────────────────────────────────────────────────────────────────────
// Audio engine — lazy AudioContext, unlocked by first user gesture
// ────────────────────────────────────────────────────────────────────────────
let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let audioUnlocked = false;
let unlockHandlersInstalled = false;

function ensureAudio(): AudioContext | null {
  if (!isBrowser()) return null;
  try {
    if (!audioCtx) {
      const Ctx: typeof AudioContext | undefined =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return null;
      audioCtx = new Ctx();
      masterGain = audioCtx.createGain();
      masterGain.gain.value = 0.05; // very low master
      masterGain.connect(audioCtx.destination);
    }
    return audioCtx;
  } catch {
    return null;
  }
}

function tryUnlockAudio() {
  const ctx = ensureAudio();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    ctx.resume().then(() => {
      audioUnlocked = true;
    }).catch(() => {});
  } else {
    audioUnlocked = true;
  }
}

function installUnlockHandlers() {
  if (unlockHandlersInstalled || !isBrowser()) return;
  unlockHandlersInstalled = true;
  const handler = () => {
    tryUnlockAudio();
    if (audioUnlocked) {
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("touchstart", handler);
      window.removeEventListener("keydown", handler);
      window.removeEventListener("click", handler);
    }
  };
  window.addEventListener("pointerdown", handler, { passive: true });
  window.addEventListener("touchstart", handler, { passive: true });
  window.addEventListener("keydown", handler);
  window.addEventListener("click", handler, { passive: true });
}

if (isBrowser()) {
  installUnlockHandlers();
}

// ────────────────────────────────────────────────────────────────────────────
// Sound primitives — synthesized, very short, very quiet
// ────────────────────────────────────────────────────────────────────────────
type ToneSpec = {
  freq: number;
  type?: OscillatorType;
  duration: number; // seconds
  peak?: number;    // gain peak (0..1) before master
  attack?: number;  // seconds
  release?: number; // seconds (decay tail)
  detune?: number;
};

function playTone(spec: ToneSpec) {
  const ctx = ensureAudio();
  if (!ctx || !masterGain || !audioUnlocked) return;
  try {
    const t0 = ctx.currentTime;
    const peak = spec.peak ?? 0.6;
    const attack = spec.attack ?? 0.004;
    const release = spec.release ?? Math.max(0.02, spec.duration - attack);
    const total = attack + release;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = spec.type ?? "sine";
    osc.frequency.value = spec.freq;
    if (spec.detune) osc.detune.value = spec.detune;

    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + total);

    osc.connect(gain);
    gain.connect(masterGain);
    osc.start(t0);
    osc.stop(t0 + total + 0.02);
    osc.onended = () => {
      try { osc.disconnect(); } catch {}
      try { gain.disconnect(); } catch {}
    };
  } catch {
    /* ignore */
  }
}

function playSequence(tones: ToneSpec[], gapMs = 50) {
  let delay = 0;
  for (const t of tones) {
    setTimeout(() => playTone(t), delay);
    delay += Math.round(t.duration * 1000) + gapMs;
  }
}

// NOTE: The legacy synthesized "beep" sound recipes have been removed in
// favor of the premium showroom audio engine in `src/lib/audioBus.ts`.
// `feedback.*` calls now fire haptics only; any audio cues are emitted by
// audioBus through site-wide data-sound delegation. This keeps the brand's
// quiet, gallery-grade sonic identity consistent everywhere.
const SOUND_RECIPES: Record<FeedbackType, () => void> = {
  light: () => {}, selection: () => {}, tick: () => {}, medium: () => {},
  success: () => {}, warning: () => {}, error: () => {},
  open: () => {}, close: () => {},
  spinStart: () => {}, spinTick: () => {}, spinEnd: () => {},
};

// ────────────────────────────────────────────────────────────────────────────
// Vibration patterns
// ────────────────────────────────────────────────────────────────────────────
const VIB_PATTERNS: Record<FeedbackType, number | number[]> = {
  light: 8,
  selection: 10,
  tick: 6,
  medium: 16,
  success: [12, 30, 18],
  warning: [20, 40, 20],
  error: [35, 40, 35],
  open: 12,
  close: 10,
  spinStart: 10,
  spinTick: 5,
  spinEnd: 14,
};

// per-type minimum interval in ms
const MIN_INTERVAL: Record<FeedbackType, number> = {
  light: 60,
  selection: 55,
  tick: 50,
  medium: 90,
  success: 180,
  warning: 200,
  error: 240,
  open: 120,
  close: 120,
  spinStart: 120,
  spinTick: 55,
  spinEnd: 140,
};

// ────────────────────────────────────────────────────────────────────────────
// Optional Capacitor Haptics (loaded only if available)
// ────────────────────────────────────────────────────────────────────────────
type CapHaptics = {
  impact?: (opts: { style: string }) => Promise<void>;
  selectionStart?: () => Promise<void>;
  selectionChanged?: () => Promise<void>;
  selectionEnd?: () => Promise<void>;
  notification?: (opts: { type: string }) => Promise<void>;
};
let capHaptics: CapHaptics | null = null;
let capTried = false;

async function tryLoadCapacitor() {
  if (capTried || !isBrowser()) return;
  capTried = true;
  try {
    const cap = (window as any).Capacitor;
    if (!cap?.isNativePlatform?.()) return;
    // Only attempt to load when running inside a real Capacitor shell.
    // Use a runtime-resolved name + Function() to keep Vite from trying
    // to pre-bundle "@capacitor/haptics" in the web build.
    const modName = "@capacitor/haptics";
    const dynImport = new Function("m", "return import(m)") as (m: string) => Promise<any>;
    const mod = await dynImport(modName).catch(() => null);
    if (mod?.Haptics) capHaptics = mod.Haptics as CapHaptics;
  } catch {
    /* ignore */
  }
}
if (isBrowser()) tryLoadCapacitor();

function fireCapacitor(type: FeedbackType) {
  if (!capHaptics) return;
  try {
    switch (type) {
      case "light":
      case "tick":
      case "spinTick":
        capHaptics.impact?.({ style: "LIGHT" });
        break;
      case "selection":
      case "spinStart":
      case "spinEnd":
        capHaptics.selectionChanged?.();
        break;
      case "medium":
      case "open":
      case "close":
        capHaptics.impact?.({ style: "MEDIUM" });
        break;
      case "success":
        capHaptics.notification?.({ type: "SUCCESS" });
        break;
      case "warning":
        capHaptics.notification?.({ type: "WARNING" });
        break;
      case "error":
        capHaptics.notification?.({ type: "ERROR" });
        break;
    }
  } catch {
    /* ignore */
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Public dispatcher
// ────────────────────────────────────────────────────────────────────────────
function fire(type: FeedbackType) {
  if (!isBrowser()) return;
  if (!readPref(KEY_FEEDBACK, true)) return;

  const reduce = prefersReducedMotion();
  // In reduced-motion mode, only allow direct confirmation cues — drop spin & passive cues.
  if (reduce && (type === "spinStart" || type === "spinTick" || type === "spinEnd" || type === "tick")) {
    return;
  }

  const t = now();
  const interval = MIN_INTERVAL[type] ?? 60;
  const lastForType = lastSoundByType[type] ?? 0;
  if (t - lastForType < interval) return;
  lastSoundByType[type] = t;

  const hapticsOn = readPref(KEY_HAPTICS, true);
  const soundOn = readPref(KEY_SOUND, true);

  // 1. Native Capacitor (if present)
  if (hapticsOn) fireCapacitor(type);

  // 2. Web Vibration (Android, etc.). iOS Safari silently no-ops.
  if (hapticsOn && hasVibrate() && isTouchDevice() && !reduce) {
    if (t - lastVibAt >= 40) {
      lastVibAt = t;
      try {
        navigator.vibrate(VIB_PATTERNS[type]);
      } catch {
        /* ignore */
      }
    }
  }

  // 3. Premium micro sound (also serves as iPhone fallback)
  if (soundOn && audioUnlocked) {
    if (t - lastSoundAt >= 20) {
      lastSoundAt = t;
      try {
        SOUND_RECIPES[type]();
      } catch {
        /* ignore */
      }
    }
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────────────
export const feedback = {
  light:     () => fire("light"),
  selection: () => fire("selection"),
  tick:      () => fire("tick"),
  medium:    () => fire("medium"),
  success:   () => fire("success"),
  warning:   () => fire("warning"),
  error:     () => fire("error"),
  open:      () => fire("open"),
  close:     () => fire("close"),
  spinStart: () => fire("spinStart"),
  spinTick:  () => fire("spinTick"),
  spinEnd:   () => fire("spinEnd"),

  cancel() {
    if (hasVibrate()) {
      try { navigator.vibrate(0); } catch { /* ignore */ }
    }
  },

  // Settings
  setFeedbackEnabled(on: boolean) { writePref(KEY_FEEDBACK, on); },
  setSoundEnabled(on: boolean)    { writePref(KEY_SOUND, on); },
  setHapticsEnabled(on: boolean)  { writePref(KEY_HAPTICS, on); },
  isFeedbackEnabled() { return readPref(KEY_FEEDBACK, true); },
  isSoundEnabled()    { return readPref(KEY_SOUND, true); },
  isHapticsEnabled()  { return readPref(KEY_HAPTICS, true); },

  // Diagnostics
  isAudioUnlocked() { return audioUnlocked; },
  unlockAudio() { tryUnlockAudio(); },
};

export default feedback;
